import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress,
    LinearProgress,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import hostService from '../services/hostService';
import { toast } from 'react-toastify';

const AddHostDialog = ({ open, onClose, onHostAdded }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [bulkResult, setBulkResult] = useState(null);
    const [progress, setProgress] = useState(null);
    const [isStopping, setIsStopping] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formik = useFormik({
        initialValues: {
            credentials: '',
        },
        validationSchema: Yup.object({
            credentials: Yup.string()
                .required('Vui lòng nhập thông tin tài khoản')
                .test('format', 'Định dạng chưa đúng: email|password|2fa_secret (mỗi dòng 1 tài khoản)', (value) => {
                    if (!value) return false;
                    const lines = value.split('\n').filter(line => line.trim());
                    return lines.every(line => line.split('|').length === 3);
                }),
        }),
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);
            setBulkResult(null);
            setProgress(null);
            setIsStopping(false);
            
            let pollingInterval = null;

            try {
                const lines = values.credentials.split('\n').filter(line => line.trim());
                
                if (lines.length > 1) {
                    // Start polling progress
                    pollingInterval = setInterval(async () => {
                        try {
                            const res = await hostService.getBulkProgress();
                            if (res.success) {
                                setProgress(res.data);
                            }
                        } catch (err) {}
                    }, 1500);

                    // Xử lý hàng loạt
                    const res = await hostService.bulkAddHosts(lines);
                    
                    clearInterval(pollingInterval);
                    setProgress(null);

                    if (res.data.failed === 0 && !res.isCancelled) {
                        toast.success(`Đã thêm thành công tất cả ${res.data.success} tài khoản`);
                        onHostAdded();
                        onClose();
                        formik.resetForm();
                    } else {
                        setBulkResult(res.data);
                        onHostAdded(); 
                        if (res.isCancelled) {
                            toast.warning('Đã dừng tiến trình theo yêu cầu');
                        } else if (res.data.success > 0) {
                            toast.info(`Đã hoàn thành: ${res.data.success} thành công, ${res.data.failed} thất bại`);
                        }
                    }
                } else {
                    // Xử lý đơn như cũ
                    await hostService.addHost({
                        credentials: lines[0],
                    });
                    toast.success('Thêm tài khoản thành công');
                    onHostAdded();
                    onClose();
                    formik.resetForm();
                }
            } catch (err) {
                if (pollingInterval) clearInterval(pollingInterval);
                setProgress(null);
                setError(err.response?.data?.message || 'Có lỗi xảy ra khi thêm tài khoản');
            } finally {
                setLoading(false);
                setIsStopping(false);
            }
        },
    });

    const handleCancel = async () => {
        try {
            setIsStopping(true);
            await hostService.cancelBulkAdd();
            toast.info('Đang gửi lệnh dừng...');
        } catch (err) {
            toast.error('Không thể gửi lệnh dừng');
            setIsStopping(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setBulkResult(null);
            setError(null);
            setProgress(null);
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle>Thêm Tài Khoản GPT Host</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    {bulkResult && bulkResult.failed > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                                Kết quả xử lý hàng loạt:
                            </Typography>
                            <Box sx={{ mt: 1, maxHeight: 150, overflow: 'auto' }}>
                                {bulkResult.details.map((item, idx) => (
                                    <Typography key={idx} variant="caption" display="block" color={item.success ? 'success.main' : 'error.main'}>
                                        • {item.credentials}: {item.success ? 'Thành công' : `Lỗi: ${item.error}`}
                                    </Typography>
                                ))}
                            </Box>
                        </Alert>
                    )}

                    {progress && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Đang xử lý: {progress.processed}/{progress.total} tài khoản
                                </Typography>
                                <Typography variant="caption" fontWeight="bold" color="primary">
                                    {Math.round((progress.processed / progress.total) * 100)}%
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={(progress.processed / progress.total) * 100} 
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                <Typography variant="caption" color="success.main">Thành công: {progress.success}</Typography>
                                <Typography variant="caption" color="error.main">Thất bại: {progress.failed}</Typography>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Nhập thông tin theo định dạng: <b>email|password|2fa_secret</b>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                            Có thể nhập nhiều dòng để thêm hàng loạt. Mỗi dòng là 1 tài khoản.
                        </Typography>

                        <TextField
                            fullWidth
                            multiline
                            rows={10}
                            id="credentials"
                            name="credentials"
                            label="Thông tin tài khoản (Hỗ trợ nhiều dòng)"
                            placeholder="email|password|2fa_secret&#13;&#10;email2|password2|2fa_secret2"
                            value={formik.values.credentials}
                            onChange={formik.handleChange}
                            error={formik.touched.credentials && Boolean(formik.errors.credentials)}
                            helperText={formik.touched.credentials && formik.errors.credentials}
                            disabled={loading}
                            sx={{ 
                                '& .MuiInputBase-input': { 
                                    fontFamily: 'monospace',
                                    fontSize: '0.9rem',
                                    lineHeight: 1.5
                                } 
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    {loading ? (
                        <Button 
                            color="error" 
                            onClick={handleCancel} 
                            disabled={isStopping || (progress && progress.isCancelled)}
                        >
                            {isStopping ? 'Đang dừng...' : 'Dừng ngay'}
                        </Button>
                    ) : (
                        <Button onClick={handleClose}>Hủy</Button>
                    )}
                    
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading} 
                        startIcon={loading && <CircularProgress size={20} />}
                    >
                        {loading ? 'Đang thực hiện...' : 'Xác nhận thêm'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddHostDialog;
